import type { Localization } from "../constants/localization";
import { getLocalizationLocale } from "../utils/localization-helper";

/**
 *  export imported Localization type as it was previsously defined in this file.
 */
export type { Localization };

/**
 * Implement this interface if your component has hard-coded localizable strings (i18n).
 * @example See the Link component
 */
export interface LocalizableComponent<ComponentLocalization> {
  /**
   * The host element of the custom component. Should be annotated with the Stencil decorator @Element().
   * @example @Element() hostElement: HTMLEonUiMyComponentElement;
   */
  hostElement;

  /**
   * The localization of the component for all languages.
   */
  localization: Localization<ComponentLocalization>;

  /**
   * The current localization based on the closest lang attribute.
   * Use this to set translated strings in your component.
   */
  currentLocalization: ComponentLocalization;

  /**
   * Determines the current language context of the component and updates the currentLocalization property.
   *
   * @default
   * setCurrentLocalization() {
   *   setCurrentLocalizationDelegate(this);
   * }
   */
  setCurrentLocalization(): void;
}

/**
 * Default implementation for setCurrentLocalization().
 * Determines the current language context of the component based on its own or the nearest enclosing [lang] attribute set on any DOM element.
 * If there is none, it uses "en" (English) as fallback language.
 * Finally updates the currentLocalization property with the appropriate values taken from the localization of the component.
 * @param component A component implementing LocalizableComponent<ComponentLocalization>.
 */
export function setCurrentLocalizationDelegate<ComponentLocalization>(
  component: LocalizableComponent<ComponentLocalization>
): void {
  const locale = getLocalizationLocale(component.hostElement);
  component.currentLocalization = component.localization[locale];
}

export interface DefaultLocalization {
  required: string;
}
